import {
  useDevCycleClient,
  useIsDevCycleInitialized,
} from '@devcycle/react-client-sdk';
import { useCallback, useEffect, useRef } from 'react';
import { getIsDevCycleAvailable } from 'src/hooks/useDevCycleAdapter';

interface DevCycleIdentity {
  devCycleClient: ReturnType<typeof useDevCycleClient> | null;
  isDevCycleInitialized: boolean;
}

export enum DevCycleUserIdentityStatus {
  LOADING = 'loading',
  AUTHENTICATED = 'authenticated',
  ANONYMOUS = 'anonymous',
}

export type DevCycleUserIdentity =
  | {
      status: DevCycleUserIdentityStatus.LOADING;
    }
  | {
      status: DevCycleUserIdentityStatus.ANONYMOUS;
    }
  | {
      status: DevCycleUserIdentityStatus.AUTHENTICATED;
      userId: string;
      corpId?: string;
    };

const useRealDevCycleIdentity = (): DevCycleIdentity => ({
  devCycleClient: useDevCycleClient(),
  isDevCycleInitialized: useIsDevCycleInitialized(),
});

const useNoOpDevCycleIdentity = (): DevCycleIdentity => ({
  devCycleClient: null,
  isDevCycleInitialized: true,
});

const useDevCycleIdentity = getIsDevCycleAvailable()
  ? useRealDevCycleIdentity
  : useNoOpDevCycleIdentity;

const getIdentityKey = (identity: DevCycleUserIdentity): string | null => {
  switch (identity.status) {
    case DevCycleUserIdentityStatus.ANONYMOUS:
      return DevCycleUserIdentityStatus.ANONYMOUS;
    case DevCycleUserIdentityStatus.AUTHENTICATED:
      return `${identity.userId}:${identity.corpId ?? ''}`;
    case DevCycleUserIdentityStatus.LOADING:
    default:
      return null;
  }
};

export const useIdentifyUser = (
  identity: DevCycleUserIdentity
): (() => Promise<void>) => {
  const { devCycleClient, isDevCycleInitialized } = useDevCycleIdentity();
  const lastIdentityKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!devCycleClient || !isDevCycleInitialized) return;

    const identityKey = getIdentityKey(identity);

    if (!identityKey || lastIdentityKeyRef.current === identityKey) return;

    const syncUserIdentity = async (): Promise<void> => {
      if (identity.status === DevCycleUserIdentityStatus.ANONYMOUS) {
        await devCycleClient.resetUser();
        lastIdentityKeyRef.current = identityKey;
        return;
      }

      if (identity.status !== DevCycleUserIdentityStatus.AUTHENTICATED) {
        return;
      }

      await devCycleClient.identifyUser({
        // eslint-disable-next-line camelcase -- to match DevCycle SDK
        user_id: identity.userId,
        ...(identity.corpId
          ? {
              customData: {
                corpId: identity.corpId,
              },
            }
          : {}),
      });
      lastIdentityKeyRef.current = identityKey;
    };

    syncUserIdentity().catch((error) =>
      console.error('Failed to sync DevCycle user identity:', error)
    );
  }, [devCycleClient, identity, isDevCycleInitialized]);

  return useCallback(async (): Promise<void> => {
    if (!devCycleClient || !isDevCycleInitialized) return;

    await devCycleClient.resetUser();
    lastIdentityKeyRef.current = DevCycleUserIdentityStatus.ANONYMOUS;
  }, [devCycleClient, isDevCycleInitialized]);
};
